// SPEC-VERSION-VISIBILITY-AND-UPDATE-V1 (D-456) ITEM_4/ITEM_6: ログイン画面の版表示・状態バッジ・更新ボタン。
//   現場の人が「自分のアプリが古いか」を判断でき、その場で更新できるようにする。
//   ITEM_6 の強制更新(major時のみ)もここで判定し、強制時は親(Login)がスタッフ一覧を隠す。
//   副作用(version.json 取得 / 手動チェック / 適用)は依存注入で差し替え可能=vitest 検証可能。
import { useEffect, useState, useCallback, useRef } from 'react'
import { BUILD_VERSION, BUILD_SHA, BUILD_NUMBER } from '../../lib/buildInfo'
import { manualCheckForUpdate, fetchRemoteVersion, applyUpdate, subscribeNeedRefresh, subscribeInstallState, retryInstall } from '../../lib/swRegistration'
import { isBelowMinimum } from '../../lib/appVersion'
import { fetchMinimumSupportedVersion } from '../../lib/appConfig' // D-457: 強制閾値はサーバ(app_config)から
// SPEC-UPDATE-TIPS-V1 (D-661): 更新を待つ間に使い方の一行を出す。更新の振る舞いには触らない添え物。
import UpdateTips from './UpdateTips'
import { fetchActiveTips } from '../../services/appTips'

// ITEM_6 ロックアウト防止: 強制中に更新しても版が上がらないとき、二度目の失敗で連絡してくださいと出す。
export const FORCED_ATTEMPTS_KEY = 'd456-forced-update-attempts'

// SPEC-UPDATE-PREPARING-PROGRESS-V1 (D-522) AC6: 準備が長引いていると見なす秒数。
//   ★実測(ひろ 2026-08-28、テスト版 v1.0.16)では 30〜40 秒で「今すぐ更新」に変わる。
//     よって 60 秒は観測された最遅(40 秒)の 1.5 倍で、正常な待ちでは踏まない。
//   ★誤って早く出したときの害がほぼ無いので、遅らせるより早めに倒す。
//     強調して出すのは既存と同じ「閉じて開き直す」案内で、それは正常時にやっても壊れない操作だから。
//     逆に閾値が高すぎると、D-147/D-148 で実際に起きた「永久に進まない」状態を画面が隠してしまう。
//   ★秒数そのものは常に出しているので、閾値は「案内を強めるかどうか」だけを決める。
//     ここで「壊れています」とは言わない(本当に壊れているかはこの画面からは分からない)。
export const PREPARING_LONG_MS = 60_000

// SPEC-UPDATE-ENTRY-SINGLE-POINT-V1 (D-463) ITEM_3: 更新ボタンの状態を決める純関数。
//   押せるのは「実際に適用できる」ときだけ(=SW が waiting)か、まだ確認していないときのみ。
//   入力: waiting(SW waiting の実在) / badge(version.json 由来の検知状態) / forced(強制更新中)。
//   戻り値の4状態:
//     apply     … waiting あり。押せる。「今すぐ更新」。
//     preparing … 新版あり/強制だが waiting 無し。押せない。「更新の準備中です」+閉じて開き直す案内。
//     checking  … 確認中。押せない。
//     check     … 新版が見つかっていない。押せる。「更新を確認」。
export function resolveUpdateAction({ waiting, badge, forced }) {
  if (badge === 'checking') return 'checking'
  if (waiting) return 'apply'               // waiting があれば SKIP_WAITING の送り先が居る=押せば効く
  if (forced || badge === 'update-available') return 'preparing' // 新版/強制だが適用先が未準備
  return 'check'
}

const ACTION_LABEL = {
  apply:     '今すぐ更新',
  preparing: '更新の準備中です',
  checking:  '確認中…',
  check:     '更新を確認',
}

export default function VersionPanel({
  onForcedChange,
  currentVersion = BUILD_VERSION,
  currentSha = BUILD_SHA,
  buildNumber = BUILD_NUMBER,
  checkFn = manualCheckForUpdate,
  fetchVersionFn = fetchRemoteVersion,
  minVersionFn = fetchMinimumSupportedVersion, // D-457 ITEM_5: 強制閾値の取得(サーバ app_config)
  applyFn = applyUpdate,
  subscribeFn = subscribeNeedRefresh, // D-463 ITEM_2: SW waiting 実在の購読(既存の集約層をそのまま使う)
  storage = (typeof sessionStorage !== 'undefined' ? sessionStorage : null),
  transientMs = 3000, // D-463 ITEM_6 三: 手動チェックの結果(最新です/確認できませんでした)を出す時間(目安3秒)
  tipsLoadFn = fetchActiveTips, // D-661: Tips の取得(この画面の他の副作用と同じく差し替え可能にする)
  tipsRandom = Math.random,     // D-661: 開始位置のランダム化(テストで固定する)
  installSubscribeFn = subscribeInstallState, // D-1041: 新しい SW の install の状態
  retryFn = retryInstall,                     // D-1041:「もう一度準備する」= registration.update() だけ
}) {
  const [install, setInstall] = useState('idle') // D-1041: 'idle'|'installing'|'installed'|'failed'
  useEffect(() => {
    const unsub = installSubscribeFn((st) => setInstall(st?.phase || 'idle'))
    return () => { if (typeof unsub === 'function') unsub() }
  }, [installSubscribeFn])
  const [badge, setBadge] = useState('idle') // idle|checking|latest|update-available|error(表示はしない=ボタン状態の入力)
  const [forced, setForced] = useState(false)
  const [lockedOut, setLockedOut] = useState(false)
  const [waiting, setWaiting] = useState(false) // D-463 ITEM_2: SW waiting の実在
  const [transient, setTransient] = useState(null) // D-463 ITEM_6: 手動チェック直後だけ出す一過性メッセージ
  const transientTimerRef = useRef(null)
  const onForcedRef = useRef(onForcedChange)
  onForcedRef.current = onForcedChange

  // D-463 ITEM_6: 一過性メッセージ(最新です/確認できませんでした)を transientMs だけ出して自動で消す。
  const showTransient = useCallback((msg) => {
    setTransient(msg)
    if (transientTimerRef.current) clearTimeout(transientTimerRef.current)
    transientTimerRef.current = setTimeout(() => setTransient(null), transientMs)
  }, [transientMs])
  useEffect(() => () => { if (transientTimerRef.current) clearTimeout(transientTimerRef.current) }, [])

  // D-463 ITEM_2: 既存の subscribeNeedRefresh を購読するだけ(registration を直接持たない・検知層は足さない)。
  //   subscribeNeedRefresh は購読した瞬間に現在値で即コールバックするので、既存の waiting もその場で拾う。
  useEffect(() => {
    const unsub = subscribeFn(setWaiting)
    return () => { if (typeof unsub === 'function') unsub() }
  }, [subscribeFn])

  // マウント時: 二本立て(D-457)。version.json=新版があるか(sha) / app_config=使わせないか(強制閾値)。
  //   どちらか片方が失敗しても、もう片方の判定は生かす。強制側は失敗したら常に false(ロックアウト防止)。
  useEffect(() => {
    let alive = true
    ;(async () => {
      const [verOutcome, minVer] = await Promise.all([
        // version.json: 取得可否とペイロードを分けて持つ(失敗はバッジのみに影響、強制には影響させない)。
        fetchVersionFn().then(r => ({ ok: true, r })).catch(() => ({ ok: false, r: null })),
        // 強制閾値: fetchMinimumSupportedVersion は null 安全だが、呼び出し自体の失敗も null に倒す。
        Promise.resolve().then(minVersionFn).catch(() => null),
      ])
      if (!alive) return
      // 強制判定は app_config の閾値のみ(version.json の minimum_supported は読まない=DECISIONS 二)。
      //   minVer が null(取得失敗/欠落/不正)なら isBelowMinimum は false=強制しない。
      const below = isBelowMinimum(currentVersion, minVer)
      setForced(below)
      onForcedRef.current?.(below)
      if (below) {
        // ロックアウト防止: 既に2回試して版が上がっていなければ、押させず連絡導線のみ。
        const attempts = Number(storage?.getItem(FORCED_ATTEMPTS_KEY) || 0)
        if (attempts >= 2) setLockedOut(true)
        setBadge('update-available')
      } else {
        storage?.removeItem(FORCED_ATTEMPTS_KEY) // 強制されない=カウンタを掃除。
        // バッジは version.json(sha)で決める。取得失敗は「確認できませんでした」。
        if (verOutcome.ok) {
          setBadge(verOutcome.r?.sha && verOutcome.r.sha !== currentSha ? 'update-available' : 'latest')
        } else {
          setBadge('error')
        }
      }
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 「更新を確認」: 即答せず manualCheckForUpdate(数秒 waiting ポーリング)に委ねる。
  //   D-463 ITEM_6 三: 押した結果を一定時間だけ出す。latest=最新です / error=確認できませんでした。
  //   update-available はボタンが「今すぐ更新/更新の準備中です」に変わる=それ自体が告知なので一過性メッセージは出さない。
  const runCheck = useCallback(async () => {
    setBadge('checking')
    const { status } = await checkFn()
    setBadge(status) // 'update-available' | 'latest' | 'error'(表示はボタン/一過性メッセージ側)
    if (status === 'latest') showTransient('最新です')
    else if (status === 'error') showTransient('確認できませんでした')
  }, [checkFn, showTransient])

  // 「今すぐ更新」: 既存 applyUpdate(明示タップ適用)を呼ぶ。強制中は試行回数を数える。
  const doApply = useCallback(() => {
    if (forced) {
      const attempts = Number(storage?.getItem(FORCED_ATTEMPTS_KEY) || 0)
      storage?.setItem(FORCED_ATTEMPTS_KEY, String(attempts + 1))
    }
    applyFn()
  }, [forced, applyFn, storage])

  // D-463 ITEM_3/ITEM_6: 押せる/押せないと文言は resolveUpdateAction に一元化(waiting 連動)。真実源は1つ=矛盾しない。
  const action = resolveUpdateAction({ waiting, badge, forced }) // apply|preparing|checking|check

  // D-522: preparing の間だけ経過秒数を数える。
  //   ★SW のインストール進捗を取る API は存在しない(registration.installing は state の遷移しか見えない)。
  //     割合を出すと嘘になるので、出すのは「回っているスピナー」と「実測の経過秒数」だけにする。
  //   ★tick を数えず Date.now() の差を使う。バックグラウンドで setInterval が間引かれても秒数がずれない。
  //   ★依存は action だけ。preparing を抜けた瞬間に cleanup が走ってタイマーが必ず止まる(AC4)。
  const [preparingSec, setPreparingSec] = useState(0)
  useEffect(() => {
    if (action !== 'preparing') {
      setPreparingSec(0)
      return
    }
    setPreparingSec(0)
    const startedAt = Date.now()
    const id = setInterval(() => setPreparingSec(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(id)
  }, [action])
  const preparingLong = action === 'preparing' && preparingSec * 1000 >= PREPARING_LONG_MS
  const enabled = action === 'apply' || action === 'check'
  // D-1041 (1): install の状態は適用できる(apply)/確認中(checking)以外のときに、ボタンとは別の要素で出す。
  //   ★version-action の文言(D-463 の契約)と version-prepare-note(D-522: 失敗と断定しない)には混ぜない。
  const showInstall = action === 'preparing' || action === 'check'
  const handleRetry = useCallback(() => { try { Promise.resolve(retryFn()).catch(() => {}) } catch { /* 表示だけの導線。失敗しても画面は止めない */ } }, [retryFn])
  const isPrimary = action === 'apply'

  return (
    <div data-testid="version-panel" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {/* 一 現在の版(人間が読める大きさ) */}
      <div data-testid="version-current" style={{ fontSize: 18, fontWeight: 700, color: '#e8e8f0' }}>
        v{currentVersion}
      </div>

      {/* D-463 ITEM_6: 常時表示のバッジは廃止(真実源をボタン1つに集約=最新です と 今すぐ更新 の同時表示を構造的に排除)。 */}

      {/* ITEM_6(D-456) 強制メッセージ */}
      {forced && (
        <div data-testid="version-forced-note" style={{ fontSize: 13, color: '#f87171', textAlign: 'center', maxWidth: 300 }}>
          この版は古すぎて使えません。更新してください。
        </div>
      )}

      {/* 三 更新ボタン(D-463: 押せるのは apply/check のときだけ。preparing/checking は無反応にせず disabled) */}
      {lockedOut ? (
        <button
          data-testid="version-contact"
          disabled
          style={{ fontSize: 14, fontWeight: 700, padding: '8px 20px', borderRadius: 10, border: 'none', background: '#3a3a45', color: '#c8c8d0', minHeight: 44 }}
        >
          更新できませんでした。サポートに連絡してください
        </button>
      ) : (
        <>
          <button
            data-testid="version-action"
            data-action={action}
            onClick={action === 'apply' ? doApply : action === 'check' ? runCheck : undefined}
            disabled={!enabled}
            style={{
              fontSize: 14, fontWeight: 700, padding: '8px 20px', borderRadius: 10, border: 'none',
              background: isPrimary ? '#f0c040' : enabled ? '#2a2a35' : '#3a3a45',
              color: isPrimary ? '#1a1a1a' : enabled ? '#e8e8f0' : '#8a8a98',
              minHeight: 44,
              opacity: enabled ? 1 : 0.7,
            }}
          >
            {ACTION_LABEL[action]}
          </button>
          {/* D-522: preparing のあいだだけ、回っているスピナーと実測の経過秒数を出す。
              ★割合や進捗バーは出さない(SW に進捗 API が無いので数字を作れば嘘になる)。
              ★ボタンの文言(version-action の textContent)は D-463 の契約なので 1 文字も変えない。
                秒数はボタンの外に置く。 */}
          {action === 'preparing' && (
            <>
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              <div
                data-testid="version-preparing-progress"
                role="status"
                aria-live="polite"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#9090a8' }}
              >
                <span
                  data-testid="version-preparing-spinner"
                  aria-hidden="true"
                  style={{
                    width: 12, height: 12, border: '2px solid #9090a8', borderTopColor: 'transparent',
                    borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block',
                  }}
                />
                <span data-testid="version-preparing-elapsed">{preparingSec}秒</span>
              </div>
            </>
          )}

          {/* D-1041 (1): 新しい版の取り込み(SW install)の状態。進捗の割合は出せないので状態名だけ。 */}
          {showInstall && install === 'installing' && (
            <div data-testid="version-install-state" data-phase="installing" style={{ fontSize: 12, color: '#c8c8d0', textAlign: 'center' }}>
              新しい版を読み込み中です
            </div>
          )}
          {/* D-1041 (1): install が失敗(redundant)。「もう一度準備する」は update() を呼ぶだけ(reload しない)。 */}
          {showInstall && install === 'failed' && (
            <div data-testid="version-install-failed" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, maxWidth: 300 }}>
              <div style={{ fontSize: 13, color: '#f0c040', fontWeight: 700, textAlign: 'center' }}>
                新しい版の準備に失敗しました。電波の良い所で「もう一度準備する」を押してください
              </div>
              <button
                type="button"
                data-testid="version-install-retry"
                onClick={handleRetry}
                style={{ fontSize: 14, fontWeight: 700, padding: '8px 20px', borderRadius: 10, border: 'none', background: '#2a2a35', color: '#e8e8f0', minHeight: 44 }}
              >
                もう一度準備する
              </button>
            </div>
          )}

          {/* SPEC-UPDATE-TIPS-V1 (D-661): 更新を待っている間だけ、使い方の一行を出す。
              ★出す条件は「更新がある」= apply(押せば適用できる)/ preparing(準備待ち)。
                確認していないだけ(check)や確認中(checking)では出さない。
              ★ボタンの下に別の要素として置く。ボタンのタップ領域は 1px も削らない(spec FORBIDDEN)。
              ★0 件 / 取得失敗なら何も描画しない = ここが空でも上の更新導線は従来どおり動く。 */}
          <UpdateTips
            active={action === 'apply' || action === 'preparing'}
            loadFn={tipsLoadFn}
            random={tipsRandom}
          />

          {/* ITEM_3: preparing(新版はあるが waiting 未準備)のときは、確実に効く方法を案内する。
              ★この案内は D-522 でも消さない。スピナーは「動いている」ことを見せるだけで、
                確実に効く逃げ道はこちら(D-147/D-148 で永久停止が実際に起きている)。
              ★長引いたときは色と太さを上げて目立たせるだけ。文言の主旨は変えない。 */}
          {action === 'preparing' && (
            <div
              data-testid="version-prepare-note"
              data-long={preparingLong ? 'true' : 'false'}
              style={{
                fontSize: 12, color: preparingLong ? '#f0c040' : '#9090a8', textAlign: 'center', maxWidth: 300,
                fontWeight: preparingLong ? 700 : 400,
              }}
            >
              {preparingLong && '時間がかかっています。'}
              {/* D-1041 (3): 先に「開いたまま待つ」。閉じると取り込み(install)が中断されうる。開き直しは次の手。 */}
              この画面を開いたまま少し待ってください。それでも変わらないときは、アプリを一度閉じて開き直してください
            </div>
          )}
        </>
      )}

      {/* D-463 ITEM_6 三: 手動チェックの結果(最新です/確認できませんでした)だけを一定時間だけ出す(マウント時は出さない)。 */}
      {transient && (
        <div data-testid="version-transient" style={{ fontSize: 12, color: '#9090a8' }}>
          {transient}
        </div>
      )}

      {/* build # ・ sha は消さず小さく下に残す(不具合追跡に必要) */}
      <div data-testid="version-build" style={{ fontSize: 11, color: '#5a5a6a' }}>
        build #{buildNumber} · {currentSha}
      </div>
    </div>
  )
}
